import { useEffect, useRef, useState } from "react";
import { Rocket, Users, TrendingUp, DollarSign } from "lucide-react";

const stats = [
  { icon: Rocket, value: 21, suffix: "+", label: "Startups Funded", desc: "early-stage ventures backed by AQT's investor community" },
  { icon: Users, value: 117, suffix: "+", label: "Active Investors", desc: "a thriving network of angels & wealth creators" },
  { icon: TrendingUp, value: 13, suffix: " Cr+", label: "Successful Exits", desc: "proven outcomes and profitable exits" },
  { icon: DollarSign, value: 31, suffix: " Cr+", label: "Pooled Fund", desc: "structured capital deployment via SPVs and syndicates" },
];

// Custom hook for count-up animation
const useCountUp = (target: number, start: boolean, duration: number = 1500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startValue = 0;
    const increment = target / (duration / 16);
    let animationFrameId: number;

    const animate = () => {
      startValue += increment;
      if (startValue < target) {
        setCount(Math.floor(startValue));
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [start, target, duration]);

  return count;
};

// Separate component for stat card
interface StatCardProps {
  item: typeof stats[0];
  index: number;
  visible: boolean;
}

const StatCard = ({ item, index, visible }: StatCardProps) => {
  const Icon = item.icon;
  const count = useCountUp(item.value, visible);

  return (
    <div
      className={`
        bg-tertiary p-8 rounded-2xl border border-borderLight
        transition-all duration-700 ease-out group
        hover:shadow-[0_0_20px_rgba(212,175,55,0.6)]
        hover:border-[#D4AF37]
        transform
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
      `}
      style={{ transitionDelay: `${index * 200}ms` }}
    >
      {/* ICON (Styled EXACTLY like screenshot) */}
      <div
        className="
          w-14 h-14 
          rounded-xl 
          flex items-center justify-center 
          bg-[linear-gradient(to_right,#22c1c3,#fdbb2d)]
          shadow-[inset_0_0_12px_rgba(255,255,255,0.08)] 
          border border-white/10 
          group-hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] 
          transition-all duration-300
          mb-4
        "
      >
        <Icon className="w-7 h-7 text-white group-hover:scale-110 transition-transform" />
      </div>

      {/* COUNT NUMBER */}
      <div className="text-4xl font-bold mb-2 text-white">
        {count}
        {item.suffix}
      </div>

      {/* LABEL */}
      <div className="text-lg font-semibold text-white mb-2">
        {item.label}
      </div>

      {/* DESCRIPTION */}
      <p className="text-textMuted text-sm leading-relaxed">
        {item.desc}
      </p>
    </div>
  );
};

export default function About() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Detect when section is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (!hasAnimated) setHasAnimated(true);
          } else {
            setVisible(false);
          }
        });
      },
      { threshold: 0.3, rootMargin: "50px" }
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasAnimated]);

  return (
    <section
      id="about-us"
      ref={sectionRef}
      className="py-20 px-6 bg-primary text-white scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto">

        {/* Heading */}
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#f2eee3] text-center">
          ABOUT US
        </h2>

        {/* ABOUT TEXT */}
        <div className="space-y-6 text-lg text-textMuted leading-relaxed mb-16">
          <p>We are an exclusive, founder-focused platform designed to connect startups with the right investors at every stage of their fundraising journey.</p>
          <p>Our unified ecosystem enables founders to pitch their business ideas to investors who truly align with their vision, values, and long-term growth plans—no matter their background or experience level.</p>
          <p>We support founders from the earliest stages by strengthening their business model, refining their strategy, and helping them become investment-ready.</p>
          <p>
            Unlike traditional fundraising channels, we do not demand excessive equity or hidden charges.{" "}
            {/* <span className="text-white font-semibold">just a simple, one-time pitching fee of ₹4,99.</span> */}
          </p>
           <p className="text-lg text-textMuted leading-relaxed mb-12">
          With access to a powerful network of investors, mentors, and industry experts, we help startups accelerate their growth, build meaningful relationships, and unlock real funding opportunities.
        </p> 
        </div>
         

        {/* STATS GRID */}
<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
  {stats.map((item, index) => (
    <StatCard
      key={index}
      item={item}
      index={index}
      visible={visible}
    />
  ))}
</div>
      </div>
    </section>
  );
}
